import CakeOutlinedIcon from '@mui/icons-material/CakeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import MedicalInformationOutlinedIcon from '@mui/icons-material/MedicalInformationOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import WaterDropOutlinedIcon from '@mui/icons-material/WaterDropOutlined';
import { Skeleton } from '@mui/material';
import React, { FC, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  SnapshotTileKey,
  SnapshotTileState,
} from '../../../common/model/type/clinical-snapshot.type';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

const TILE_ICONS: Record<SnapshotTileKey, ReactElement> = {
  age: <CakeOutlinedIcon />,
  healthGoal: <FlagOutlinedIcon />,
  reproductiveStage: <TimelineOutlinedIcon />,
  lastPeriodStart: <WaterDropOutlinedIcon />,
  knownReproductiveDisorder: <MedicalInformationOutlinedIcon />,
  trackingPeriod: <CalendarMonthOutlinedIcon />,
};

const TILE_ACCENTS: Record<SnapshotTileKey, { fg: string; bg: string }> = {
  age: { fg: Colours.OV_ACCENT_BLUE, bg: Colours.OV_LIGHT_BLUE },
  healthGoal: { fg: Colours.OV_ACCENT_GREEN, bg: Colours.OV_FERTILE },
  reproductiveStage: {
    fg: Colours.OV_ACCENT_INDIGO,
    bg: Colours.OV_PEAK_OVULATION,
  },
  lastPeriodStart: {
    fg: Colours.OV_ACCENT_MAGENTA,
    bg: Colours.OV_PERIOD_FLOW,
  },
  knownReproductiveDisorder: {
    fg: Colours.OV_ACCENT_AMBER,
    bg: Colours.OV_HIGH_OVULATION,
  },
  trackingPeriod: {
    fg: Colours.OV_ACCENT_TEAL,
    bg: Colours.OV_ACCENT_TEAL_TINT,
  },
};

interface OvClinicalSnapshotTileProps {
  tileKey: SnapshotTileKey;
  state: SnapshotTileState;
}

const OvClinicalSnapshotTile: FC<OvClinicalSnapshotTileProps> = ({
  tileKey,
  state,
}) => {
  const { t } = useTranslation();

  let content: ReactElement;

  switch (state.status) {
    case 'loading':
      content = <Skeleton variant="text" width="4.5rem" height="1.25rem" />;
      break;

    case 'value':
      content = (
        <Value>
          {state.value}
          {state.secondary && <Secondary>{state.secondary}</Secondary>}
        </Value>
      );
      break;

    case 'error':
      content = (
        <ErrorValue>
          <ErrorOutlineIcon fontSize="small" aria-hidden="true" />
          {t('patientDetails.snapshot.unavailable')}
        </ErrorValue>
      );
      break;

    case 'notEnoughScans':
      content = (
        <EmptyValue>
          {t(`patientDetails.snapshot.empty.${tileKey}.notEnoughScans`, {
            scanDays: state.scanDays,
          })}
        </EmptyValue>
      );
      break;

    default:
      content = (
        <EmptyValue>
          {t(`patientDetails.snapshot.empty.${tileKey}.${state.status}`)}
        </EmptyValue>
      );
  }

  return (
    <Tile>
      <ValueRow>
        <TileIcon $fg={TILE_ACCENTS[tileKey].fg} $bg={TILE_ACCENTS[tileKey].bg}>
          {TILE_ICONS[tileKey]}
        </TileIcon>
        <ValueSlot>{content}</ValueSlot>
      </ValueRow>
      <Label>{t(`patientDetails.snapshot.tiles.${tileKey}`)}</Label>
    </Tile>
  );
};

export default OvClinicalSnapshotTile;

const Tile = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 0.75rem;
  min-width: 0;
`;

const ValueRow = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  min-height: 1.5rem;
`;

const TileIcon = styled.span<{ $fg: string; $bg: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: ${Variables.borderRadius.MEDIUM};
  color: ${({ $fg }) => $fg};
  background: ${({ $bg }) => $bg};

  .MuiSvgIcon-root {
    width: 1rem;
    height: 1rem;
  }
`;

const ValueSlot = styled.span`
  flex: 1;
  min-width: 0;
  padding-top: 0.125rem;
  line-height: ${Variables.lineHeights.LARGE};
`;

const Value = styled.span`
  font-size: ${Variables.fontSizes.MEDIUM};
  font-weight: bold;
  color: ${Colours.OV_BASE};
  overflow-wrap: anywhere;
`;

const Secondary = styled.span`
  display: block;
  font-weight: normal;
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_PRUSSIAN_BLUE};
`;

const ErrorValue = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: ${Variables.fontSizes.MEDIUM};
  color: ${Colours.OV_ACCENT_AMBER};
  overflow-wrap: anywhere;

  .MuiSvgIcon-root {
    flex-shrink: 0;
    width: 1rem;
    height: 1rem;
  }
`;

const EmptyValue = styled.span`
  font-size: ${Variables.fontSizes.MEDIUM};
  font-style: italic;
  color: ${Colours.OV_PRUSSIAN_BLUE};
  overflow-wrap: anywhere;
`;

const Label = styled.span`
  display: block;
  margin-top: 0.5rem;
  font-size: ${Variables.fontSizes.SMALL};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${Colours.OV_PRUSSIAN_BLUE};
`;
